import React from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from "@mui/material";

const ScopeAccreditationCalibration = ({ data, labType }) => {
  if (!data || data.length === 0) return null;
  // GROUP DATA BY ASSESSOR + DISCIPLINE
  const groupedData = data.reduce((acc, item) => {
    const key = `${item.AssessorName}_${item.disciplineName}`;

    if (!acc[key]) {
      acc[key] = {
        assessor: item.AssessorName,
        discipline: item.disciplineName,
        group: item.groupName,
        scopes: []
      };
    }

    acc[key].scopes.push(item);
    return acc;
  }, {});

  // Maintain continuous serial number across all groups
  let serialNumber = 0;

  return (
   <Box sx={{ mb: 4 }}>
      <Card elevation={3}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            bgcolor: "#e3f2fd",
            p: 1,
            mb: 2,
            textAlign: "center"
          }}
        >
          Recommended Scope of Accreditation
        </Typography>

        {Object.values(groupedData).map((group, gIndex) => (
          <Box key={gIndex} sx={{ mb: 3, px: 2 }}>

            {/* ASSESSOR + DISCIPLINE HEADER */}
            <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
              Assessor: {group.assessor || "-"}
            </Typography>

            <Typography sx={{ fontWeight: "bold", mb: 1 }}>
              Discipline: {group.discipline || "-"}
            </Typography>

            <Typography sx={{ fontWeight: "bold", mb: 1 }}>
              Group: {group.group || "-"}
            </Typography>

            <TableContainer component={Paper}>
              <Table
                size="small"
                sx={{
                  border: "1px solid #bdbdbd",
                  "& th, & td": { border: "1px solid #bdbdbd" },
                  borderCollapse: "collapse"
                }}
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                    <TableCell align="center" rowSpan={2} sx={{ fontWeight: "bold", minWidth: 40 }}>Sl</TableCell>
                    <TableCell align="center" rowSpan={2} sx={{ fontWeight: "bold", minWidth: 180 }}>Parameter*/Device under Calibration</TableCell>
                    <TableCell align="center" rowSpan={2} sx={{ fontWeight: "bold", minWidth: 120 }}>Master Equipment used</TableCell>
                    <TableCell align="center" rowSpan={2} sx={{ fontWeight: "bold", minWidth: 120 }}>Range(s) of Measurement</TableCell>
                    <TableCell align="center" colSpan={3} sx={{ fontWeight: "bold" }}>Calibration and Measurement Capability</TableCell>
                    <TableCell align="center" rowSpan={2} sx={{ fontWeight: "bold", minWidth: 120 }}>Remarks/Method used</TableCell>
                    <TableCell align="center" rowSpan={2} sx={{ fontWeight: "bold", minWidth: 100 }}>Calibration Performed At</TableCell>
                    <TableCell align="center" rowSpan={2} sx={{ fontWeight: "bold", minWidth: 80 }}>Status</TableCell>
                    <TableCell align="center" rowSpan={2} sx={{ fontWeight: "bold", minWidth: 120 }}>Assessment Technique Used</TableCell>
                    <TableCell align="center" rowSpan={2} sx={{ fontWeight: "bold", minWidth: 120 }}>Remarks</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                    <TableCell align="center" sx={{ fontWeight: "bold", minWidth: 100 }}>Claimed by Laboratory</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", minWidth: 100 }}>Observed by Assessor</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", minWidth: 100 }}>Recommended by Assessor</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {group.scopes.map((item, index) => {
                    serialNumber++; 
                    return (
                    <TableRow key={index}>
                      <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                        {serialNumber}
                      </TableCell>

                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        <div>{item.GroupTested || ""}</div>
                        {item.testCode && <div style={{ fontSize: "0.8rem", color: "#666", marginTop: 3 }}>(Test Code: {item.testCode})</div>}
                        {item.TypeOfTest && <div style={{ fontSize: "0.8rem", color: "#666", marginTop: 3 }}>(Calibration Performed: {item.TypeOfTest})</div>}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {item.EquipmentName || ""}
                      </TableCell>

                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {(item.RangeUL && item.RangeUL !== "")
                          ? `${item.RangeLL || ""} ${item.RangeLLUnit || ""} To ${item.RangeUL} ${item.RangeULUnit || ""}`
                          : `${item.RangeLL || ""} ${item.RangeLLUnit || ""}`}
                      </TableCell>

                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {item.cv ? `${item.cv} ${item.mu}` : ''} 
                        {item.cmcuppervalue ? ` to ${item.cmcuppervalue} ${item.cmcupperUnit}` : ''} 
                        {!item.cv && !item.cmcuppervalue ? '' : ''}
                      </TableCell>

                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        <span style={{ 
                          color: item.conclusion?.toLowerCase() === "competent" ? "#2e7d32" : item.conclusion ? "#c62828" : "inherit",
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}>
                          {item.conclusion || ""}
                        </span>
                        {item.observation && (
                          <div style={{ color: "#555", fontSize: "0.8rem", marginTop: 2 }}>
                            {item.observation}
                          </div>
                        )}
                        {item.calibrationTaken && (
                          <div style={{ fontSize: "0.8rem", color: "#666", marginTop: 2 }}>
                            Calibration Points: {item.calibrationTaken}
                          </div>
                        )}
                      </TableCell>

                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {item.CMC || (item.cv ? `${item.cv} ${item.mu}` : '') || ''}
                        {item.cmcuppervalue && !item.CMC ? ` to ${item.cmcuppervalue} ${item.cmcupperUnit}` : ''}
                      </TableCell>

                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {item.scopeStandard || ""}
                      </TableCell>

                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {item.testPerformedAt && (
                          <div style={{ fontSize: "0.8rem", color: "#666", marginTop: 2 }}>
                            {item.testPerformedAt}
                          </div>
                        )}
                      </TableCell>

                      <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                        <Chip
                          label={item.status || "N/A"}
                          size="small"
                          sx={{
                            backgroundColor:
                              item.status === "Adequate"
                                ? "#4caf50"
                                : item.status === "Inadequate"
                                ? "#f44336"
                                : item.status === "Not Applicable"
                                ? "#ff9800"
                                : "#9e9e9e",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "0.75rem"
                          }}
                        />
                      </TableCell>

                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {item.assessmentTechniqueUsed || item.AssessmentTechniqueUsed || ""}
                      </TableCell>

                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {item.Remarks || ""}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

          </Box>
        ))}
      </Card>
    </Box>
  );
};

export default ScopeAccreditationCalibration;

// import React from "react";
// import {
//   Box,
//   Card,
//   Typography,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Paper,
//   Chip
// } from "@mui/material";

// const ScopeAccreditationCalibration = ({ data, labType }) => {
//   if (!data || data.length === 0) return null;
//   // GROUP DATA BY ASSESSOR + DISCIPLINE
//   const groupedData = data.reduce((acc, item) => {
//     const key = `${item.AssessorName}_${item.disciplineName}`;

//     if (!acc[key]) {
//       acc[key] = {
//         assessor: item.AssessorName,
//         discipline: item.disciplineName,
//         group: item.groupName,
//         scopes: []
//       };
//     }

//     acc[key].scopes.push(item);
//     return acc;
//   }, {});

//   // Maintain continuous serial number across all groups
//   let serialNumber = 0;

//   return (
//    <Box sx={{ mb: 4 }}>
//       <Card elevation={3}>
//         <Typography
//           variant="h6"
//           sx={{
//             fontWeight: "bold",
//             bgcolor: "#e3f2fd",
//             p: 1,
//             mb: 2,
//             textAlign: "center"
//           }}
//         >
//           Recommended Scope of Accreditation
//         </Typography>

//         {Object.values(groupedData).map((group, gIndex) => (
//           <Box key={gIndex} sx={{ mb: 3, px: 2 }}>

//             {/* ASSESSOR + DISCIPLINE HEADER */}
//             <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
//               Assessor: {group.assessor || "-"}
//             </Typography>

//             <Typography sx={{ fontWeight: "bold", mb: 1 }}>
//               Discipline: {group.discipline || "-"}
//             </Typography>

//             <Typography sx={{ fontWeight: "bold", mb: 1 }}>
//               Group: {group.group || "-"}
//             </Typography>

//             <TableContainer component={Paper}>
//               <Table
//                 size="small"
//                 sx={{
//                   border: "1px solid #bdbdbd",
//                   "& th, & td": { border: "1px solid #bdbdbd" },
//                   borderCollapse: "collapse"
//                 }}
//               >
//                 <TableHead>
//                   <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
//                     <TableCell align="center"><b>Sr No.</b></TableCell>
//                     <TableCell align="center"><b>Scope Details</b></TableCell>
//                     <TableCell align="center"><b>Range & Standards</b></TableCell>
//                     <TableCell align="center"><b>Calibration and Measurement Capability</b></TableCell>
//                     <TableCell align="center"><b>Assessment Details</b></TableCell>
//                     <TableCell align="center"><b>Status</b></TableCell>
//                   </TableRow>
//                 </TableHead>

//                 <TableBody>
//                   {group.scopes.map((item, index) => {
//                     serialNumber++; // Increment serial number for each scope
//                     return (
//                     <TableRow key={index}>
//                       <TableCell align="center">
//                         {serialNumber}
//                       </TableCell>

//                       <TableCell>
//                         <div><strong>Test Code:</strong> {item.testCode || "-"}</div>
//                         <div><strong>Parameter/Device under Calibration:</strong> {item.GroupTested || "-"}</div>
//                         <div><strong>Master Equipment used:</strong> {item.EquipmentName || "-"}</div>
//                         <div><strong>Calibration Performed:</strong> {item.TypeOfTest || "-"}</div>
//                         <div><strong>Remarks/Method used:</strong> {item.scopeStandard || "-"}</div>
//                       </TableCell>

//                       <TableCell>
//                         <div><strong>Type:</strong> {item.rangeType || "-"}</div>
//                         <div><strong>Facility:</strong> {item.testPerformedAt || "-"}</div>
//                         <div>
//                             <strong>Range(s) of Measurement:</strong>{" "}
//                             {(item.RangeUL && item.RangeUL==="")
//                               ? `${item.RangeLL || "-"} ${item.RangeLLUnit || ""} To ${item.RangeUL} ${item.RangeULUnit || ""}`
//                               : `${item.RangeLL || "-"} ${item.RangeLLUnit || ""}`}
//                           </div>
//                         <div><strong>CMC:</strong> {item.cv ? `LL: ${item.cv}${item.mu}` : ''} {item.cmcuppervalue ? `UL: ${item.cmcuppervalue}${item.cmcupperUnit}` : ''} {!item.cv && !item.cmcuppervalue ? '-' : ''}</div>
//                       </TableCell>

//                       <TableCell>
//                         <div><strong>Claimed by Laboratory:</strong> {item.cv ? `CMC LL: ${item.cv}${item.mu}` : ''} {item.cmcuppervalue ? `CMC UL: ${item.cmcuppervalue}${item.cmcupperUnit}` : ''} {!item.cv && !item.cmcuppervalue ? '-' : ''}</div>
//                         <div style={{ marginBottom: 4 }}>
//                           <strong>Observed by Assessor:</strong>{" "}
//                           <strong style={{ color: item.conclusion?.toLowerCase() === "competent" ? "#2e7d32" : item.conclusion ? "#c62828" : "inherit", textTransform:'capitalize' }}>
//                             {item.conclusion || "-"}
//                           </strong>
//                           {item.observation && (
//                             <span style={{ color: "#555", fontStyle: "italic", marginLeft: 4 }}>
//                               ({item.observation})
//                             </span>
//                           )}
//                         </div>
//                         <div>
//                           <div style={{marginTop: 2 }}>
//                             <div><strong>Calibration Points Taken:</strong> {item.calibrationTaken || "-"}</div>
//                             <div><strong>Recommended CMC by the Assessor:</strong> {item.CMC ? item.CMC : item.cv ? `LL: ${item.cv}${item.mu}` : ''} {item.cmcuppervalue ? `CMC UL: ${item.cmcuppervalue}${item.cmcupperUnit}` : ''} {!item.cv && !item.cmcuppervalue ? '-' : ''}</div>
//                           </div>
//                         </div>
//                       </TableCell>

//                       <TableCell>
//                         <div><strong>Assessment Technique:</strong> {item.assessmentTechniqueUsed || item.scopeStandard || "-"}</div>
//                         {item.Remarks && (
//                           <div><strong>Remarks:</strong> {item.Remarks}</div>
//                         )}
//                       </TableCell>

//                       <TableCell align="center">
//                         <Chip
//                           label={item.status || "N/A"}
//                           size="small"
//                           sx={{
//                             backgroundColor:
//                               item.status === "Adequate"
//                                 ? "#4caf50"
//                                 : item.status === "Inadequate"
//                                 ? "#f44336"
//                                 : item.status === "Not Applicable"
//                                 ? "#ff9800"
//                                 : "#9e9e9e",
//                             color: "#fff",
//                             fontWeight: 700
//                           }}
//                         />
//                       </TableCell>
//                     </TableRow>
//                     );
//                   })}
//                 </TableBody>
//               </Table>
//             </TableContainer>

//           </Box>
//         ))}
//       </Card>
//     </Box>
//   );
// };

// export default ScopeAccreditationCalibration;
